import { useEffect, useMemo, useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePartnerConnectStatus } from '@/hooks/usePartnerConnectStatus';
import { customerPay, type PayoutPrompt } from '@/lib/partnerJobs';
import { partnerConnectPresentation } from '@/lib/partnerConnectPresentation';
import { payoutSetupCopy } from '@/lib/payoutSetupCopy';
import { payoutLocale } from '@/lib/sitePayouts';
import { X, Loader2 } from 'lucide-react';
import {
  startAgentConnect, getDisplayErrorMessage,
  type AgentRow, type PartnerConnectStatus,
} from '@/lib/api';
import {
  EMPTY_AGENT_PAYOUT_CHOICE, agentApiTakesCountry, agentConnectStartBody, agentStartCountryProblem,
  agentStartCountryStopMessage, canStartAgentConnect,
  type AgentPayoutChoice,
} from '@/lib/agentPayoutCountry';
import { Modal } from '@/components/ui/Modal';
import formStyles from '@/components/ui/Form.module.css';
import { AgentPayoutCountryField } from './AgentPayoutCountryField';
import styles from './SalesPartnerConnectBanner.module.css';
import { PayoutSupportMessage } from './PayoutSupportMessage';

/** Payout setup is asked for only when money is owed (Warren, final ruling 1), and stays until Stripe confirms readiness. */

interface Props {
  /** Hydrated Agent row from listAgents (SP-scoped per Phase 7g.4a,
   *  returns array-of-one). Null while the query is in flight or
   *  if the SP has no bound Agent doc (a guarded edge case the
   *  consumer surfaces separately). */
  agent: AgentRow | null;
  /** Bound Agent _id from PublicUser.agent_id (Phase 7g.4b.1).
   *  Null on the same edge cases as agent. The startAgentConnect
   *  CTA short-circuits when null. */
  agentId: string | null;
  /** Optional callback fired whenever the visible banner count
   *  changes. Consumers use this to size content top-padding so
   *  the layout reserves space for the fixed banner stack. Counts:
   *  0 (no banners), 1 (one of the variants), 2 (success
   *  stacked above persistent). SP Dashboard uses 60px per banner. */
  onVisibleBannerCountChange?: (count: number) => void;
  /** Profile-incomplete signal (is_sales_agent true but agent_id
   *  null , data drift / pre-provisioning). Prop-driven because in
   *  this state the Agent row itself is null, so the banner can't
   *  derive it from `agent`. When true the banner renders the
   *  profile_incomplete variant EVEN THOUGH agent is null (the
   *  normal "no agent → silent" guard is bypassed). Highest
   *  priority over the agent-derived variants. Transitional: once
   *  admin role-change provisions the SP profile this state is
   *  rare; it's the defensive replacement for the old full-screen
   *  "not fully set up" early-return on the SP pages. */
  profileIncomplete?: boolean;
  /** Sales staff reuse the same country confirmation and banner component. */
  startConnect?: typeof startAgentConnect;
  /** Sales staff keep this banner until Stripe confirms transfers and bank payouts. */
  payoutsReady?: boolean;
  connectStatus?: PartnerConnectStatus;
  appearance?: 'default' | 'card';
  /** What a rep or SP Manager is owed (Api: /admin/sales-book/me payout_prompt). A Sales Partner's is read here
   *  (Api: GET /admin/partner-jobs/payout-prompt). */
  owed?: PayoutPrompt | null;
  /** The quiet row for someone who wants to set up payouts before money is owed (SPEC_PAYOUT_SETUP_WHEN_MONEY:
   *  "Payouts: set up before your first payment. You can do it now or later."). Only the partner's profile and
   *  Reports pass it; Dashboard, My chargers and Commissions show nothing until money is owed. */
  early?: boolean;
}

export function SalesPartnerConnectBanner({
  agent, agentId, onVisibleBannerCountChange, profileIncomplete = false,
  startConnect = startAgentConnect,
  payoutsReady,
  connectStatus,
  appearance = 'default',
  owed: staffOwed = null,
  early = false,
}: Props) {
  const isPartner = startConnect === startAgentConnect;
  const locale = payoutLocale();
  const text = payoutSetupCopy(locale);
  const status = usePartnerConnectStatus(agentId, isPartner);
  const presentation = partnerConnectPresentation(isPartner ? status.data : connectStatus, !!agent?.stripe_connect_account_id, locale);
  const confirmedReady = isPartner ? !!status.data && !status.isError && presentation.ready : payoutsReady === true;
  // Payout setup is asked for only when a commission or session share is owed, with the amount (Warren, final ruling 1,
  // 21 October 2026, night; walkthrough rank 18), for a Sales Partner, a rep and an SP Manager alike.
  const partnerOwed = useQuery({ queryKey: ['partner-payout-prompt', agentId], queryFn: customerPay.payoutPrompt, enabled: isPartner && !!agentId && !confirmedReady,
    staleTime: 60_000, retry: false });
  const owed = isPartner ? partnerOwed.data ?? null : staffOwed;
  // When what is owed cannot be read, the setup shows as before rather than hide money waiting.
  const owedUnknown = isPartner && partnerOwed.isError;
  // Connect-success dismissible banner state. On mount: parse
  // ?connect=success from the URL. If present, show the banner +
  // clear the URL param via history.replaceState so a refresh
  // doesn't re-trigger. Dismiss button hides the banner without
  // touching the URL (already cleared at mount time).
  const [showConnectSuccess, setShowConnectSuccess] = useState(false);
  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get('connect') === 'success') {
        if (isPartner && (status.isPending || status.isFetching)) return;
        setShowConnectSuccess(confirmedReady);
        sp.delete('connect');
        const next = window.location.pathname
          + (sp.toString() ? `?${sp.toString()}` : '');
        window.history.replaceState({}, '', next);
      }
    } catch {
      /* SSR / sandboxed contexts; no-op */
    }
  }, [isPartner, status.isPending, status.isFetching, confirmedReady]);

  // A stored account alone does not confirm payout readiness.
  const bannerKind: 'profile_incomplete' | 'deactivated' | 'connect_setup' | 'connect_early' | null = useMemo(() => {
    // profile_incomplete is prop-driven (agent is null in this
    // state) and outranks every agent-derived variant.
    if (profileIncomplete) return 'profile_incomplete';
    if (!agent) return null;
    if (!agent.active) return 'deactivated';
    // Never before money is owed (Warren, final ruling 1, 21 October 2026, night). Where the page asks for it, a quiet
    // row instead, once the status read has answered (no flash while it loads).
    if (!confirmedReady) {
      if (owed?.due || owedUnknown) return 'connect_setup';
      const settled = isPartner ? !status.isPending && !partnerOwed.isPending : true;
      return early && settled ? 'connect_early' : null;
    }
    return null;
  }, [profileIncomplete, agent, confirmedReady, owed?.due, owedUnknown, early, isPartner, status.isPending, partnerOwed.isPending]);

  // Notify the consumer of visible banner count so it can size its
  // top-padding for the fixed banner stack. Fires on mount + on
  // every state change that flips banner visibility.
  const visibleCount =
    (showConnectSuccess && confirmedReady ? 1 : 0)
    + (bannerKind !== null ? 1 : 0);
  useEffect(() => {
    onVisibleBannerCountChange?.(visibleCount);
  }, [visibleCount, onVisibleBannerCountChange]);

  // Connect-start handler state + dispatch. handleStartConnect runs
  // when the SP clicks the connect-setup CTA; redirects to the
  // Stripe-hosted onboarding URL on success. Error state surfaces
  // inline under the banner copy via the existing
  // getDisplayErrorMessage helper.
  //
  // New accounts need an explicit country; existing accounts resume onboarding.
  const [connectBusy, setConnectBusy] = useState(false);
  const [connectErr, setConnectErr] = useState('');
  const [countryOpen, setCountryOpen] = useState(false);
  // The partner's existing payout account, linked instead of a trip to support (Warren, 21 October 2026).
  const [existingUrl, setExistingUrl] = useState('');
  const [payoutChoice, setPayoutChoice] = useState<AgentPayoutChoice>(EMPTY_AGENT_PAYOUT_CHOICE);
  const hasAccount = !!agent?.stripe_connect_account_id;
  const takesCountry = agentApiTakesCountry(agent);
  const openCountryDialog = (): void => {
    setConnectErr('');
    setPayoutChoice(EMPTY_AGENT_PAYOUT_CHOICE);
    setCountryOpen(true);
  };
  const closeCountryDialog = (): void => {
    if (!connectBusy) setCountryOpen(false);
  };
  const handleStartConnect = async (): Promise<void> => {
    if (!agentId || connectBusy || !canStartAgentConnect(hasAccount, payoutChoice, takesCountry)) return;
    setConnectErr('');
    setConnectBusy(true);
    try {
      const body = agentConnectStartBody(hasAccount, payoutChoice);
      const { url, country } = await startConnect(agentId, body);
      // Stripe opens only for an account in the chosen country (a lost
      // race or an older Api can answer with one in the other country).
      const problem = agentStartCountryProblem(body?.country, country);
      if (body && problem) {
        setConnectBusy(false);
        setConnectErr(agentStartCountryStopMessage('self', body.country, problem, locale));
        setExistingUrl(url);
        return;
      }
      window.location.href = url;
    } catch (err) {
      setConnectBusy(false);
      setConnectErr(await getDisplayErrorMessage(err, text.spStartFailed));
    }
  };

  const resumed = useRef(false);
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    if (query.get('connect') !== 'refresh' || resumed.current || !agentId || !hasAccount) return;
    resumed.current = true;
    query.delete('connect');
    window.history.replaceState({}, '', window.location.pathname + (query.size ? `?${query}` : ''));
    void handleStartConnect();
  });

  // No agent AND not profile-incomplete → nothing to render. The
  // profile_incomplete state deliberately renders WITH a null agent
  // (it IS the "is_sales_agent true but no Agent doc" case), so it
  // bypasses this guard. Every other path with a null agent stays
  // silent (initial-fetch race), since the page now renders its
  // shell + map regardless and the banner is the only setup signal.
  if (!agent && !profileIncomplete) return null;

  // Fragment return: consumer wraps in its own .bannerStack so the
  // position:fixed layout rules stay co-located with the page that
  // owns the surrounding flow.
  return (
    <>
      {bannerKind === 'profile_incomplete' && (
        <div className={`${styles.banner} ${styles.bannerDanger} ${appearance === 'card' ? styles.cardBanner : ''}`}>
          <span className={styles.bannerText}>
            {text.spProfileIncomplete}
          </span>
        </div>
      )}

      {showConnectSuccess && confirmedReady && (
        <div className={`${styles.banner} ${styles.bannerSuccess} ${appearance === 'card' ? styles.cardBanner : ''}`}>
          <span className={styles.bannerText}>
            {text.spSetupComplete}
          </span>
          <button
            type="button"
            className={styles.bannerClose}
            aria-label={text.dismiss}
            onClick={() => setShowConnectSuccess(false)}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {bannerKind === 'deactivated' && (
        <div className={`${styles.banner} ${styles.bannerDanger} ${appearance === 'card' ? styles.cardBanner : ''}`}>
          <span className={styles.bannerText}>
            {text.spDeactivated}
          </span>
        </div>
      )}

      {(bannerKind === 'connect_setup' || bannerKind === 'connect_early') && (
        <div className={`${styles.banner} ${bannerKind === 'connect_early' ? styles.bannerQuiet : styles.bannerDanger} ${appearance === 'card' ? styles.cardBanner : ''}`}>
          <div className={styles.bannerBody}>
            <span className={styles.bannerText}>
              {/* "You have $X waiting. Set up payouts to receive it." (Warren, final ruling 1, 21 October 2026, night).
                  Before money is owed, the quiet row's words instead. */}
              {bannerKind === 'connect_early' ? text.spSetUpEarly : owed?.line ?? presentation.message}
            </span>
            {connectErr && (
              <span className={styles.bannerError}><PayoutSupportMessage text={connectErr} locale={locale} /></span>
            )}
          </div>
          {presentation.button && <button
            type="button"
            className={styles.bannerCta}
            onClick={() => { if (hasAccount) void handleStartConnect(); else openCountryDialog(); }}
            disabled={connectBusy}
          >
            {/* Opens the in-app country dialog; only its Continue to
                Stripe button leaves the app (and shows progress). */}
            {presentation.button}
          </button>}
        </div>
      )}

      <Modal
        isOpen={countryOpen && (bannerKind === 'connect_setup' || bannerKind === 'connect_early')}
        onClose={closeCountryDialog}
        title={text.spDialogTitle}
        size="sm"
        footer={(
          <>
            <button
              type="button"
              className={formStyles.btnSecondary}
              onClick={closeCountryDialog}
              disabled={connectBusy}
            >
              {text.cancel}
            </button>
            <button
              type="button"
              className={formStyles.btnPrimary}
              onClick={() => void handleStartConnect()}
              disabled={connectBusy || !canStartAgentConnect(hasAccount, payoutChoice, takesCountry)}
            >
              {connectBusy
                ? <><Loader2 size={12} className={styles.spin} /> {text.spOpeningStripe}</>
                : text.spContinueToStripe}
            </button>
          </>
        )}
      >
        <div className={styles.dialogBody}>
          <p className={formStyles.hint}>
            {text.spDialogIntro}
          </p>
          <AgentPayoutCountryField
            idPrefix="sp-connect"
            subject="self"
            value={payoutChoice}
            onChange={setPayoutChoice}
            updating={!takesCountry}
            disabled={connectBusy}
            locale={locale}
          />
          {connectErr && <div className={formStyles.formError} role="alert"><PayoutSupportMessage text={connectErr} locale={locale} /></div>}
          {existingUrl && <a href={existingUrl}>{text.spContinueToStripe}</a>}
        </div>
      </Modal>
    </>
  );
}
